import React, { useMemo, useState } from "react";
import { Box, Stack, Typography, useTheme, CircularProgress, Alert, Tooltip, Paper, Button, IconButton, Collapse, Divider, Chip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { buildPcapngMulti, downloadBlob } from "../../../utils/pcap";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import TimelinePlayer from "../../../components/TimelinePlayer";
import MiniNetwork from "../../../components/MiniNetwork";
import { AckButton } from "../../../components/columns";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Europe/Rome";

function safeStr(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function safeNum(v, def = 0) {
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : def;
}

function formatTs(ts) {
  if (!ts) return "-";
  const d = dayjs(ts);
  return d.isValid() ? d.tz(TZ).format("DD/MM/YYYY HH:mm:ss") : "-";
}

function formatTick(ts) {
  if (!ts) return "-";
  const d = dayjs(ts);
  return d.isValid() ? d.tz(TZ).format("DD/MM HH:mm") : "-";
}

function formatEndpoint(ip, port) {
  const i = safeStr(ip);
  if (!i || i === "-") return "-";
  const p = port === null || port === undefined || port === "" ? "" : safeStr(port);
  return p ? `${i}:${p}` : i;
}

function extractMitreNames(mitreValue) {
  if (!mitreValue) return [];

  const asArray = Array.isArray(mitreValue) ? mitreValue : [mitreValue];
  const out = [];

  for (const item of asArray) {
    if (!item) continue;

    if (typeof item === "string") {
      const s = item.trim();
      if (s) out.push(s);
      continue;
    }

    if (typeof item === "object") {
      const candidate =
        item?.name ??
        item?.tactic_name ??
        item?.technique_name ??
        item?.tactic ??
        item?.technique ??
        item?.display_name ??
        item?.id ??
        item?.technique_id ??
        item?.tactic_id ??
        "";

      if (typeof candidate === "string") {
        const s = candidate.trim();
        if (s) out.push(s);
      }
    }
  }

  // dedup (case-sensitive to keep original labels)
  return Array.from(new Set(out));
}

function getColorBySeverity(severity) {
  const s = safeNum(severity, -1);

  if (s === 1) return "#931A1D";
  if (s === 0) return "#FEA51D";
  if (s === 2) return "#C9B85E";
  if (s === 3) return "#58A8AE";

  return "#999999";
}

function getSeverityLabel(severity) {
  const s = safeNum(severity, -1);

  if (s === 1) return "Critical";
  if (s === 0) return "High";
  if (s === 2) return "Alert";
  if (s === 3) return "Movement";

  return "Unknown";
}

const CompactField = ({ label, value, noWrap = true }) => {
  return (
    <Box sx={{ minWidth: 150 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={800}
        sx={{ textTransform: "uppercase", letterSpacing: 0.35 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ lineHeight: 1.15, fontWeight: 800, color: "text.primary" }}
        title={value || "-"}
        noWrap={noWrap}
      >
        {value || "-"}
      </Typography>
    </Box>
  );
};

const SeverityBadge = ({ severity }) => {
  const label = getSeverityLabel(severity);
  const color = getColorBySeverity(severity);
  return (
    <Chip
      size="small"
      label={label}
      variant="outlined"
      sx={{
        height: 22,
        borderRadius: 999,
        borderColor: color,
        color,
        fontWeight: 800,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// EventsTimelineView — thin wrapper around the reusable <TimelinePlayer/>.
// The aggregations page renders this with header, mini-network and event card
// around the generic player. All animation/perf logic lives in TimelinePlayer.
// ─────────────────────────────────────────────────────────────────────────────
const EventsTimelineView = ({
  row,
  events = [],
  loading = false,
  error = "",
  onBack = () => {},
  onAckEvent = () => {},
}) => {
  const theme = useTheme();
  const [activeEvent, setActiveEvent] = useState(null);

  const handleAckActiveEvent = async (row, shouldAck) => {
    await onAckEvent(row, shouldAck);
    setActiveEvent((prev) =>
      prev && prev.id === row.id ? { ...prev, acked: shouldAck } : prev
    );
  };
  const [showMoreAggregateFields, setShowMoreAggregateFields] = useState(false);

  // Stable list of items with timestamps coerced to Date.
  const eventList = useMemo(
    () =>
      (events || [])
        .map((e) => {
          const ts = e?.timestamp;
          const parsed = dayjs(ts);
          const date = parsed.isValid() ? parsed.toDate() : null;
          if (!date) return null;
          return {
            ...e,
            id: e?.id ?? `${safeStr(ts)}-${safeStr(e?.flow_id)}-${safeStr(e?.signature)}`,
            timestamp: date,
          };
        })
        .filter(Boolean),
    [events]
  );

  // For the mini-network: events "up to and including" the active one.
  const activeIdx = useMemo(() => {
    if (!activeEvent) return -1;
    return eventList.findIndex((e) => e.id === activeEvent.id);
  }, [activeEvent, eventList]);

  const activeItems = useMemo(() => {
    if (activeIdx < 0) return [];
    return eventList.slice(0, activeIdx + 1);
  }, [eventList, activeIdx]);

  // MITRE + aggregate fields (carried over from the previous version).
  const mitreData = useMemo(() => {
    if (!row) return { tactics: [], techniques: [] };
    const raw = row?.raw || row || {};
    const rawEventData = raw?.eventData || raw?.specific_fields?.event_data || null;
    const candidates = [raw?.mitre, rawEventData?.mitre, raw?.eventData, rawEventData, raw].filter(Boolean);
    const pick = (sel) => { for (const c of candidates) { const v = sel(c); if (v != null) return v; } return null; };
    const t = pick((c) => c?.tactics ?? c?.mitre_tactics ?? c?.tactic) ?? pick((c) => c?.mitre ?? c);
    const k = pick((c) => c?.techniques ?? c?.mitre_techniques ?? c?.technique) ?? pick((c) => c?.mitre ?? c);
    return { tactics: extractMitreNames(t) || [], techniques: extractMitreNames(k) || [] };
  }, [row]);

  const moreAggregateFields = useMemo(() => {
    const raw = row?.raw || row || {};
    const exclude = new Set([
      "machine_id", "response", "rule_id", "severity_text",
      "_id", "id", "case_id", "flow_ids", "group_by", "group_values",
      "updated_at", "first_seen", "last_seen", "host", "description",
      "rule_type", "rule_name", "severity", "status", "events_count",
      "mitre", "eventData", "specific_fields",
    ]);
    const fmt = (v) => {
      if (v == null) return "";
      if (typeof v === "string") return v;
      if (typeof v === "number" || typeof v === "boolean") return String(v);
      if (Array.isArray(v)) {
        const prim = v.every((x) => x == null || ["string", "number", "boolean"].includes(typeof x));
        if (prim) {
          const items = v.filter((x) => x != null).map(String);
          const head = items.slice(0, 8);
          const tail = items.length > 8 ? ` … (+${items.length - 8})` : "";
          return head.join(", ") + tail;
        }
        try { return JSON.stringify(v); } catch { return String(v); }
      }
      try { return JSON.stringify(v); } catch { return String(v); }
    };
    return Object.entries(raw)
      .filter(([k, v]) => !exclude.has(k) && v != null && v !== "")
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => ({ key: k, value: fmt(v), isJson: typeof v === "object" && v !== null && !Array.isArray(v) }))
      .filter((it) => it.value !== "");
  }, [row]);

  const flowAccent = theme.palette.info?.main || theme.palette.primary.main;

  return (
    <Box mt={2} sx={{ zoom: { xs: 0.94, md: 0.9 } }}>
      <Paper sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden", backgroundColor: theme.palette.background.paper, boxShadow: "none" }}>
        <Box sx={{ p: { xs: 2, md: 3 }, position: "relative" }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ position: "absolute", top: { xs: 12, md: 16 }, right: { xs: 12, md: 16 }, zIndex: 10 }}
          >
            {(() => {
              const total = (events || []).length;
              if (total === 0) return null;
              const withCapture = (events || []).filter((e) => e?.packet || e?.payload).length;
              const disabled = withCapture === 0;
              const tooltipBody = disabled ? (
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    No capture data available
                  </Typography>
                  <Typography variant="caption">
                    None of the {total} events in this aggregation has a stored
                    payload/packet — nothing to bundle.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    Download all packets as .pcapng
                  </Typography>
                  <Typography variant="caption">
                    {withCapture}/{total} events have capture data
                    {withCapture < total ? " (others have no payload — skipped)" : ""}
                    <br />
                    Each packet carries a Wireshark-visible comment with the detection metadata.
                  </Typography>
                </Box>
              );
              return (
                <Tooltip arrow title={tooltipBody}>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={disabled}
                      startIcon={<FileDownloadIcon />}
                      onClick={() => {
                        if (disabled) return;
                        const blob = buildPcapngMulti(events, Date.now());
                        if (!blob) return;
                        const ts = dayjs().format("YYYYMMDD-HHmmss");
                        downloadBlob(blob, `aggregation-${ts}.pcapng`);
                      }}
                    >
                      .pcapng {withCapture > 0 ? `(${withCapture})` : ""}
                    </Button>
                  </span>
                </Tooltip>
              );
            })()}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
            >
              Back
            </Button>
          </Stack>

          <Stack spacing={0.25} sx={{ mb: 1.5, pr: { xs: 10, md: 12 } }}>
            <Typography variant="h5" fontWeight={800}>Crono</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, maxWidth: 820 }}>
              Timeline of events for the selected flow (play / step / scrub).
            </Typography>
          </Stack>

          <Stack spacing={2}>
            {/* Aggregate panel */}
            <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 2, backgroundColor: "transparent" }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: "uppercase", letterSpacing: 0.35, mt: 0.25 }}>
                    Aggregate details
                  </Typography>
                  {moreAggregateFields.length ? (
                    <Tooltip arrow title={showMoreAggregateFields ? "Hide additional fields" : `Show ${moreAggregateFields.length} additional fields`}>
                      <IconButton size="small" onClick={() => setShowMoreAggregateFields((v) => !v)} sx={{ mt: 0.25, border: "1px solid", borderColor: "divider", borderRadius: 999, width: 32, height: 28 }}>
                        {showMoreAggregateFields ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>
                <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 320, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, columnGap: 2, rowGap: 1, alignItems: "start" }}>
                    <Box sx={{ gridColumn: { xs: "1 / -1", md: "1 / 2" } }}><CompactField label="Host" value={safeStr(row?.host) || "-"} /></Box>
                    <Box sx={{ gridColumn: { xs: "1 / -1", md: "2 / 3" } }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: "uppercase", letterSpacing: 0.35 }}>Severity</Typography>
                      <Box sx={{ mt: 0.35 }}><SeverityBadge severity={row?.severity} /></Box>
                    </Box>
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: "uppercase", letterSpacing: 0.35 }}>Description</Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.25, fontWeight: 800, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {safeStr(row?.description) || "-"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: { xs: "100%", lg: 360 }, flexShrink: 0 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.15 }}>MITRE ATT&CK</Typography>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <Box sx={{ flex: 1, minWidth: 160 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={800}>Tactics</Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 0.25, flexWrap: "wrap" }}>
                            {mitreData.tactics.length ? (
                              <>
                                {mitreData.tactics.slice(0, 2).map((t) => <Chip key={`tac-${t}`} size="small" label={t} sx={{ bgcolor: "rgba(0,229,255,0.12)", "& .MuiChip-label": { fontWeight: 700 } }} />)}
                                {mitreData.tactics.length > 2 ? <Chip size="small" label={`+${mitreData.tactics.length - 2}`} /> : null}
                              </>
                            ) : <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.9 }}>Not available</Typography>}
                          </Stack>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 170 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={800}>Techniques</Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 0.25, flexWrap: "wrap" }}>
                            {mitreData.techniques.length ? (
                              <>
                                {mitreData.techniques.slice(0, 2).map((t) => <Chip key={`tec-${t}`} size="small" label={t} sx={{ bgcolor: "rgba(124,255,107,0.12)", "& .MuiChip-label": { fontWeight: 700 } }} />)}
                                {mitreData.techniques.length > 2 ? <Chip size="small" label={`+${mitreData.techniques.length - 2}`} /> : null}
                              </>
                            ) : <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.9 }}>Not available</Typography>}
                          </Stack>
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
                <Collapse in={showMoreAggregateFields} timeout={180} unmountOnExit>
                  <Box sx={{ mt: 0.75, pt: 0.75, borderTop: "1px solid", borderColor: "divider" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 2, rowGap: 0.75 }}>
                      {moreAggregateFields.slice(0, 12).map((f) => (
                        <Box key={f.key} sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: "uppercase", letterSpacing: 0.3 }}>{f.key.replace(/_/g, " ")}</Typography>
                          <Typography variant="body2" sx={{ mt: 0.15, fontWeight: 700, whiteSpace: f.isJson ? "pre-wrap" : "normal", fontFamily: f.isJson ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" : "inherit", opacity: 0.95, overflowWrap: "anywhere" }}>
                            {f.value || "-"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {moreAggregateFields.length > 12 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.9, display: "block", mt: 0.75 }}>Showing 12 of {moreAggregateFields.length} fields.</Typography>
                    ) : null}
                  </Box>
                </Collapse>
              </Stack>
            </Paper>

            <Divider />

            {/* Timeline player (the new reusable component) */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: "background.default" }}>
              {loading ? (
                <Box sx={{ minHeight: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : !eventList.length ? (
                <Alert severity="info">No events found for this row.</Alert>
              ) : (
                <TimelinePlayer
                  events={eventList}
                  getColor={(e) => getColorBySeverity(e.severity)}
                  baseDurationMs={Math.max(12000, Math.min(75000, eventList.length * 600))}
                  defaultSpeed={1}
                  onActiveChange={setActiveEvent}
                  renderTooltip={(e) => (
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="body2" fontWeight={700}>{formatTs(e.timestamp)}</Typography>
                      <Typography variant="body2">{e.description || e.signature || "No notification"}</Typography>
                      <Typography variant="caption">Severity: {getSeverityLabel(e.severity)}</Typography>
                    </Box>
                  )}
                />
              )}
            </Paper>

            {/* Lower grid: mini-network + event detail card */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 420px" },
                gap: 2,
              }}
            >
              <Box>
                {eventList.length ? (
                  <MiniNetwork
                    allItems={eventList}
                    activeItems={activeItems}
                    hostLabel={row?.host}
                    focusEvent={activeEvent}
                    isPlaying={false}
                    showProgress={!!activeEvent}
                    accent={flowAccent}
                    playbackSpeed={1}
                  />
                ) : (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: "background.default" }}>
                    <Typography variant="body2" color="text.secondary">No network data.</Typography>
                  </Paper>
                )}
              </Box>

              <Box>
                {activeEvent ? (
                  <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, backgroundColor: "background.default", height: "100%", display: "flex", flexDirection: "column" }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle1" fontWeight={800}>Event</Typography>
                          <Chip size="small" label={getSeverityLabel(activeEvent.severity)} sx={{ bgcolor: getColorBySeverity(activeEvent.severity), color: "#fff", fontWeight: 800 }} />
                        </Stack>
                        <AckButton row={activeEvent} onAck={handleAckActiveEvent} />
                      </Stack>
                      <Divider />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={800}>Time</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 750 }}>{formatTick(activeEvent.timestamp)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={800}>What happened</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 750, lineHeight: 1.35 }}>
                          {activeEvent.description || activeEvent.signature || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={800}>Connection</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 800 }}>
                          {formatEndpoint(activeEvent.src_ip, activeEvent.src_port)} → {formatEndpoint(activeEvent.dest_ip, activeEvent.dest_port)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: "background.default" }}>
                    <Typography variant="body2" color="text.secondary">No event selected.</Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default EventsTimelineView;
